import React, { useEffect, useRef, useState } from 'react';

/**
 * Portal export standard: choose Filtered (as displayed) vs Complete table,
 * and Visible vs All columns when the page has a column chooser.
 */
export default function TableExportMenu({
  filteredCount = 0,
  completeCount = 0,
  showColumns = true,
  exporting = false,
  disabled = false,
  buttonStyle,
  buttonLabel = '📤 Export ▾',
  accentColor = '#1d4ed8',
  align = 'right',
  onExport,
}) {
  const [open, setOpen] = useState(false);
  const [rowsScope, setRowsScope] = useState('filtered');
  const [colsScope, setColsScope] = useState('visible');
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const busy = exporting || disabled;

  const runExport = () => {
    if (busy) return;
    setOpen(false);
    onExport?.({ rows: rowsScope, columns: showColumns ? colsScope : 'all' });
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        style={{ ...buttonStyle, opacity: busy ? 0.6 : 1 }}
        disabled={busy}
        title="Export to Excel — filtered (as displayed) or the complete table"
        onClick={() => setOpen((v) => !v)}
      >
        {exporting ? '⏳ Exporting...' : buttonLabel}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            [align]: 0,
            marginTop: 4,
            backgroundColor: 'white',
            borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            zIndex: 1200,
            minWidth: 240,
            padding: '12px 16px',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>Rows</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', cursor: 'pointer', marginBottom: 4 }}>
            <input type="radio" name="tableExportRows" checked={rowsScope === 'filtered'} onChange={() => setRowsScope('filtered')} style={{ margin: 0 }} />
            Filtered — as displayed ({filteredCount})
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', cursor: 'pointer', marginBottom: showColumns ? 10 : 12 }}>
            <input type="radio" name="tableExportRows" checked={rowsScope === 'all'} onChange={() => setRowsScope('all')} style={{ margin: 0 }} />
            Complete table ({completeCount})
          </label>
          {showColumns && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>Columns</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', cursor: 'pointer', marginBottom: 4 }}>
                <input type="radio" name="tableExportCols" checked={colsScope === 'visible'} onChange={() => setColsScope('visible')} style={{ margin: 0 }} />
                Visible columns
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', cursor: 'pointer', marginBottom: 12 }}>
                <input type="radio" name="tableExportCols" checked={colsScope === 'all'} onChange={() => setColsScope('all')} style={{ margin: 0 }} />
                All columns
              </label>
            </>
          )}
          <button
            type="button"
            onClick={runExport}
            disabled={busy}
            style={{
              width: '100%',
              padding: '8px 12px',
              backgroundColor: accentColor,
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            ⬇ Export to Excel
          </button>
        </div>
      )}
    </div>
  );
}
